import { isSessionExpired, requestCurrentUsername } from "./session-requests";

const POLL_DELAY = 60000;

export default class SessionPoller {
  private static timer: ReturnType<typeof setTimeout> | undefined;
  private static generation = 0;
  private static inFlight = false;
  private static retryDelay = 5000;
  private static handleTimeout: (() => void) | undefined;

  static beginPolling = (handleTimeout: () => void) => {
    this.cancelPolling();
    this.handleTimeout = handleTimeout;
    window.addEventListener('online', this.poll);
    this.schedule(POLL_DELAY);
  };

  private static schedule = (delay: number) => {
    clearTimeout(this.timer);
    this.timer = setTimeout(this.poll, delay);
  };

  private static poll = async () => {
    if (!this.handleTimeout || this.inFlight) return;
    clearTimeout(this.timer);
    if (!navigator.onLine) return; // The online listener resumes checks.
    const generation = this.generation;
    this.inFlight = true;
    let delay = POLL_DELAY;
    try {
      const response = await requestCurrentUsername();
      if (generation !== this.generation) return;
      if (!response?.text?.trim() || response.text.trim() === 'anonymousUser') {
        this.expire();
        return;
      }
      this.retryDelay = 5000;
    } catch (error) {
      if (generation !== this.generation) return;
      if (isSessionExpired(error)) {
        this.expire();
        return;
      }
      delay = this.retryDelay;
      this.retryDelay = Math.min(this.retryDelay * 2, POLL_DELAY);
    } finally {
      if (generation === this.generation) {
        this.inFlight = false;
        this.schedule(delay);
      }
    }
  };

  private static expire = () => {
    const handleTimeout = this.handleTimeout;
    this.cancelPolling();
    handleTimeout?.();
  };

  static cancelPolling = () => {
    ++this.generation;
    clearTimeout(this.timer);
    window.removeEventListener('online', this.poll);
    this.handleTimeout = undefined;
    this.inFlight = false;
    this.retryDelay = 5000;
  };
}
